// React
import { FunctionComponent, useState } from "react"

// UI Library
import { Button, Modal, Form, Spinner } from "react-bootstrap"

// Redux
import { connect } from "react-redux"
import { AppState } from "src/store"

//Local Components
import VehicleIncidentsMiniInfo from "../VehicleIncidentsMiniInfo/VehicleIncidentsMiniInfo"

// Interfaces
import { I_IncidentAction, I_SafetyEvent, I_SafetyEventWithApprovalHistory } from "../../interfaces"
import { I_CreateApprovalWorkflow } from "src/components/approvals/interfaces"

// Services
import VIS from "../../VehicleIncidentsService"
import approvalService from "src/components/approvals/ApprovalService"
import ActionApproval from "src/components/approvals/crud/ActionApproval"
import CreateAndActionWorkflow from "src/components/approvals/crud/CreateAndActionWorkflow"
import snackBar from "src/utilities/snackBar/SnackBar"

// Constants
import { SAFETY_EVENT_RECORD_TYPE_ID, getActionTypeId, getPendingStatusTypeId, getTemplateGroupId } from "../../constants/VehicleIncidentsApprovalConfig"

// Styles
import "./VehicleIncidentsIgnoreIncident.css"

interface Props {
  userId: number
  incidentAction: I_IncidentAction
}

// Modal for ignoring an incident, optionally starting a workflow if the event is new
const VehicleIncidentsIgnoreIncident: FunctionComponent<Props> = (props) => {
  const { userId, incidentAction } = props

  // State
  const [busy, setBusy] = useState(false)
  const [comments, setComments] = useState("")

  // Helpers from VIS
  const { setIncidentAction, retrieveEventFromMap, isWithApprovalHistory, refresh, findStepId } = VIS
  const event: I_SafetyEvent | I_SafetyEventWithApprovalHistory | null = retrieveEventFromMap(incidentAction.id || -1)

  // Resolve IDs from reference
  const ignoreActionTypeId = getActionTypeId(approvalService.reference, "Ignore")
  const pendingStatusTypeId = getPendingStatusTypeId(approvalService.reference)
  const templateGroupId = getTemplateGroupId(approvalService.reference)

  // No event found — don’t render
  if (!event) return null

  // Determine if the event is new (no approval history)
  const isNew = !isWithApprovalHistory(event)
  const { EmployeeID, ObjectID } = event

  // Handle ignore action
  const handleIgnore = async () => {
    setBusy(true)
    let success = false

    if (isNew) {
      // Build workflow payload and auto-ignore on create
      const payload: I_CreateApprovalWorkflow = {
        RecordTypeId: SAFETY_EVENT_RECORD_TYPE_ID,
        RecordId: ObjectID,
        EmployeeId: EmployeeID,
        InitiatedBy: -1,
        StatusTypeId: pendingStatusTypeId,
        ApproverUserIds: [userId],
        TemplateGroupId: templateGroupId
      }

      success = await CreateAndActionWorkflow(payload, userId, ignoreActionTypeId, comments)
    } else {
      // Ignore via action call on existing approval step
      success = await ActionApproval({
        ApprovalStepId: findStepId(event),
        ActionedBy: userId,
        ApprovalActionTypeId: ignoreActionTypeId,
        Comments: comments
      })
    }

    await refresh(VIS.currentView)

    // Notify outcome
    if (success) {
      snackBar({ type: "primary", text: "Ignored incident.", timeout: 4000 })
    } else {
      snackBar({ type: "error", text: "Failed to ignore incident.", timeout: 4000 })
    }

    setBusy(false)
  }

  return (
    <div className="VehicleIncidentsIgnoreIncident">
      <Modal
        id="vehicle-incidents-ignore-incident-modal"
        show
        backdrop="static"
        keyboard
        centered
        className="VehicleIncidentsWorkflowModal"
        animation={false}
      >
        <Modal.Header>
          <div className="VehicleIncidentsIgnoreIncidentHeader VehicleIncidentsWorkflowModalHeader">
            <p>Ignore this incident?</p>
            {busy && <Spinner
              className="VehicleIncidentsWorkflowModalHeaderSpinner"
              animation="border"
            />}
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="VehicleIncidentsIgnoreIncidentModalBody">
            <VehicleIncidentsMiniInfo
              id={incidentAction.id}
            />
            <Form.Group>
              <Form.Label>Comments</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="VehicleIncidentsIgnoreIncidentModalFooter VehicleIncidentsGenericModalFooter">
            <div className="VehicleIncidentsIgnoreIncidentModalFooterLeft">
              {/* Cancels and closes modal */}
              <Button
                variant="secondary"
                onClick={() => setIncidentAction({ action: -1, id: -1 })}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>

            <div className="VehicleIncidentsIgnoreIncidentModalFooterRight">
              {/* Triggers the ignore action */}
              <Button
                variant="primary"
                onClick={handleIgnore}
                disabled={busy}
              >
                {busy ? "Ignoring..." : "Ignore"}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

// Map Redux state to props
const mapStateToProps = (state: AppState) => ({
  userId: parseInt(state.user.userId),
})

export default connect(mapStateToProps)(VehicleIncidentsIgnoreIncident)